/**
 * Der Statusbereich — §49.
 *
 * §49 zählt auf, was neben dem Brett sichtbar sein muss: Spieler, Farben, Teams, Punktestand,
 * **aktive Person**, Bedenkzeiten, Respawn-Pools und der Spielstatus. „Aktiver Spieler muss klar
 * erkennbar sein" steht dort als eigener Satz, und deshalb trägt die Zeile eine Markierung und
 * nicht nur eine andere Farbe: eine Farbe allein ist keine Auskunft für jeden Betrachter.
 *
 * **Die Zeile ist seit Zyklus D2c ein Raster** (`ARCHITECTURE.md` **E37**): links der Name und
 * darunter eine Unterzeile, rechts die Zahlen. Was zusammengehört, steht zusammen; was man
 * vergleicht — Punkte, Uhren —, steht untereinander in einer Spalte.
 *
 * Vorher war die Zeile **eine** `·`-verbundene Zeichenkette. Das war die Antwort auf
 * zusammengelaufenen Text in den Zyklen 10b, 10d und 10e. Im Raster trennt jetzt der Abstand, und
 * die Unterzeile behält `·` — dort stehen die Angaben wirklich in einem Fluss.
 *
 * **Das Team ist ein Abzeichen, kein Block** (Entwurf „Der Rahmen um das Brett", Entscheidung 2).
 * Nach Teams zu gruppieren wäre deutlicher, zerstört aber die Zugreihenfolge: §9 verlangt, dass
 * Teammitglieder *nicht* direkt hintereinander ziehen — in der Zugreihenfolge sind Teams also
 * zwangsläufig verschränkt. Wer gruppiert, kann nicht mehr ablesen, wer als Nächster zieht.
 *
 * **Einzelpunkte je Zeile, Teampunkte einmal in der Überschrift** (§23): das Team sieht seinen
 * Stand, der individuelle Beitrag bleibt sichtbar, und dieselben zwei Zahlen stehen nicht
 * sechsmal da. Ohne Teams entfällt die Überschriftszahl ganz — sie wäre dieselbe Zahl zweimal.
 *
 * **Die Farbe kommt als Variable herein, nicht als Farbangabe.** `--sector` steht auf der Zeile,
 * das Stylesheet macht daraus einen Punkt. Die sechs Spielerfarben liegen an genau einer Stelle
 * (`board/palette.ts`), und das Stylesheet kennt keine davon.
 *
 * **Die Bedenkzeit fehlt in Variante A ganz** (Nr. 22). Ein „0:00" dort wäre eine abgelaufene
 * Uhr, und das ist etwas anderes als keine.
 *
 * **Die Punktzahl wird gezählt, nicht eingesetzt** — `count` statt `score`, damit i18next die
 * Singularform wählen kann. „1 Punkte" stand in der Sichtprüfung zu Zyklus 10i auf dem
 * Bildschirm; eine eingebaute deutsche Sonderbehandlung wäre an der ersten weiteren Sprache
 * wieder falsch.
 *
 * **Angezeigt, nicht angewandt:** über Zeitablauf und Partieende entscheidet der Server (§38).
 * Dieser Bereich stellt dar und löst nichts aus.
 */

import { Fragment, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { PieceGlyph } from '../board/PieceGlyph.js';
import { SECTOR_COLORS } from '../board/palette.js';
import { BotBadge } from '../lobby/BotBadge.js';
import { formatDuration, type PlayerStatus, type StatusView } from './status.js';

export interface StatusPanelProps {
  readonly status: StatusView;
}

export function StatusPanel({ status }: StatusPanelProps) {
  const { t } = useTranslation(['play', 'pieces', 'sectors', 'status']);

  /** Der Sieger in Worten: ein Spieler heißt beim Namen, eine Seite ist ein Team (§26). */
  const winnerLabel = (winner: string): string => {
    const player = status.players.find((candidate) => candidate.id === winner);
    return player?.name ?? t('play:teamName', { team: winner });
  };

  /**
   * Die Unterzeile: was zum Spieler gehört, aber keine Zahl ist.
   *
   * **Der Sektorname steht hier und nicht nur als Farbpunkt.** Eine Farbe allein ist keine
   * Auskunft für jeden Betrachter — §49 verlangt Spieler *und* Farben, und bei Rot-Grün-Schwäche
   * wäre der Punkt allein nichts wert.
   */
  const sub = (player: PlayerStatus): readonly string[] => [
    t(`sectors:${player.sector}`),
    ...(player.eliminated ? [t('play:eliminated')] : []),
    // **Für einen Bot entfällt die Verbindungsanzeige ersatzlos** (**E64**, Antwort 2). Er hat
    // nie einen WebSocket, `connected` ist also immer `false` — in der Sichtprüfung von
    // `V2-2e3` stand „Robo · Grün · nicht verbunden", **während er zog**. Die Angabe war nicht
    // überflüssig, sondern falsch. Das Ausscheiden bleibt: das ist eine Spielaussage (§23) und
    // hat mit der Steuerung nichts zu tun.
    ...(player.connected || player.bot ? [] : [t('play:offline')]),
  ];

  /**
   * `Vorrat: {{pieces}}` in seine beiden Hälften zerlegt (Zyklus R3).
   *
   * **Damit bleibt der Satz die einzige Quelle** — er steht weiter vollständig in den
   * Übersetzungen, und eine Sprache, die den Platzhalter woanders hinsetzt, funktioniert
   * genauso. Gebraucht wird die Zerlegung, weil zwischen die Namen jeweils ein Symbol gehört;
   * mit `{{pieces}}` als fertiger Zeichenkette ginge das nicht.
   */
  const poolParts = t('play:poolOf', { pieces: '\u0000' }).split('\u0000');

  /**
   * Der Vorrat als Knoten: Symbol, dann Name, Komma dazwischen.
   *
   * **Der Text bleibt Zeichen für Zeichen derselbe wie vorher** — ein eingebettetes SVG trägt
   * keinen. Die Zusage aus D2c („die Unterzeile bleibt ein verbundener Satz", Befund aus 10b,
   * 10d und 10e) gilt damit unverändert, und Vorlesewerkzeuge hören dasselbe wie zuvor.
   */
  const poolOf = (player: PlayerStatus) =>
    player.pool.length === 0 ? (
      t('play:poolNone')
    ) : (
      <>
        {poolParts[0]}
        {player.pool.map((piece, index) => (
          <Fragment key={piece.id}>
            {index === 0 ? '' : ', '}
            <PieceGlyph type={piece.type} sector={player.sector} />
            {t(`pieces:${piece.type}`)}
          </Fragment>
        ))}
        {poolParts[1] ?? ''}
      </>
    );

  /** Die Seiten in der Reihenfolge ihres ersten Mitglieds — dieselbe wie die der Liste. */
  const sides: readonly { readonly id: string; readonly score: number }[] = status.teams
    ? status.players.reduce<{ id: string; score: number }[]>((acc, player) => {
        if (acc.some((side) => side.id === player.side)) return acc;
        return [...acc, { id: player.side, score: player.sideScore }];
      }, [])
    : [];

  return (
    <section data-panel="status">
      <h3>
        <span>{t('play:standing')}</span>
        {sides.length === 0 ? null : (
          <span className="tally">
            {sides
              .map((side) => t('play:sideScore', { team: side.id, score: side.score }))
              .join(' · ')}
          </span>
        )}
      </h3>

      {/*
        **Das Abzeichen „regelwidrig erzeugt"** (Nr. 38, Gestaltung vom 2026-10-06).

        Oben und nicht unten: der Schaden entsteht dadurch, dass jemand eine Beobachtung aus
        einer erzwungenen Stellung **Tage später** für echt hält — dafür ist eine Zeile tief in
        der Historie zu leise. Es gehört der **Partie** und keiner Spielerzeile: dort sähe es
        aus, als hätte dieser Spieler etwas getan.

        **Mit Text, nicht nur mit Farbe** — dieselbe Regel wie beim aktiven Spieler (§49): eine
        Farbe allein ist keine Auskunft. Und der Satz darunter nennt die **Folge**, nicht nur
        den Zustand: „Achtung" wüsste in drei Tagen niemand mehr zu deuten.
      */}
      {status.ruleOverridden ? (
        <p className="overridden" role="status">
          <span className="badge" data-field="ruleOverridden">
            {t('play:ruleOverridden')}
          </span>
          <span data-field="ruleOverriddenHint">{t('play:ruleOverriddenHint')}</span>
        </p>
      ) : null}

      <ul className="standlist">
        {status.players.map((player) => (
          <li
            key={player.id}
            className="prow"
            data-player-row={player.id}
            data-active={player.active}
            // Die Farbe als Variable, nicht als Farbangabe: was daraus wird, entscheidet das
            // Stylesheet — und das kennt keine einzige Sektorfarbe.
            style={{ '--sector': SECTOR_COLORS[player.sector].piece } as CSSProperties}
          >
            <span className="swatch" aria-hidden="true" />
            <span className="who">
              <span className="name" data-field="name">
                {player.name}
              </span>
              <span className="sub" data-field="sub">
                {sub(player).join(' · ')}
                {' · '}
                {poolOf(player)}
              </span>
            </span>
            <span className="right">
              {player.active ? (
                <span className="nowmark" data-field="active">
                  {t('play:activeMark')}
                </span>
              ) : null}
              {/*
                **Das Abzeichen eines Bots** (**E64**, Antwort 2) — im `badge`-Muster, wie das
                Team; der ganze Satz steht im `aria-label`. Seit `L3a` mit Stufe, Strategie und
                ⓘ (**E104** Teilfrage 3, **E112**), aus der Lobby-Ansicht — ohne sie „Bot".
              */}
              {player.bot ? (
                <BotBadge
                  bot={player.botDescription}
                  fallbacks={player.botFallbacks}
                  ariaLabel={t('play:botPlayer')}
                />
              ) : null}
              {status.teams && player.team !== undefined ? (
                // Nur der Buchstabe: in einer Zeile mit Namen und Zahlen ist „Team A" breit, und
                // der volle Name steht für Vorlesewerkzeuge im `aria-label`.
                <span
                  className="badge"
                  data-field="team"
                  aria-label={t('play:teamName', { team: player.team })}
                >
                  {player.team}
                </span>
              ) : null}
              {/*
                **Die Zahlenspalte zeigt Zahlen, nicht Sätze.** „0 Punkte · Bedenkzeit 9:51" in
                jeder Zeile drückte die Unterzeile in den Umbruch (Sichtprüfung zu D2c) — und
                beide Worte wiederholen sich ohnehin in jeder Zeile. Der ganze Satz steht im
                `aria-label`: er ist damit nicht weg, sondern dort, wo er gebraucht wird.

                Die Pluralform bleibt deshalb trotzdem richtig gerechnet (`count`, nicht `score`)
                — „1 Punkte" stand in der Sichtprüfung zu 10i auf dem Bildschirm.
              */}
              <span
                className="pts"
                data-field="score"
                aria-label={t('play:score', { count: player.score })}
              >
                {player.score}
              </span>
              {player.remainingMs === undefined ? null : (
                <span
                  className="clock"
                  data-field="clock"
                  aria-label={t('play:time', { time: formatDuration(player.remainingMs) })}
                >
                  {formatDuration(player.remainingMs)}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <p>
        {t('play:turnNumber', { turn: status.turnNumber })}
        {status.pointTarget === undefined
          ? null
          : ` · ${t('play:pointTarget', { target: status.pointTarget })}`}
      </p>

      {/*
        Das Partieende (§26) — **zwei Sätze, nicht einer mit einer Lücke** (Zyklus R1). Ein
        Unentschieden hat keinen Sieger, und „niemand gewinnt — wer vorn lag, hatte keine
        Bedenkzeit mehr" las sich wie ein Fehler. In Variante B ist das Remis ein regulärer
        Ausgang; der Satz sagt es als eigene Aussage.

        Welcher Fall gilt, entscheidet `winner` und nicht der Grund: so bleibt ein künftiger
        weiterer Grund ohne Sieger von sich aus richtig dargestellt.
      */}
      {status.outcome === undefined ? null : (
        <p role="status">
          {status.outcome.winner === null
            ? t('play:draw', { reason: t(`play:reason.${status.outcome.reason}`) })
            : t('play:outcome', {
                winner: winnerLabel(status.outcome.winner),
                reason: t(`play:reason.${status.outcome.reason}`),
              })}
        </p>
      )}
    </section>
  );
}
